import { useState } from 'react';

import { useToastStore } from '../../../stores/toastStore';
import { OcrProviderKinds, type OcrProviderResponse } from '../api';
import { OcrProviderForm } from '../components/OcrProviderForm';
import { PlanBadge } from '../components/PlanBadge';
import {
  useCreateOcrProvider,
  useDeleteOcrProvider,
  useOcrProviders,
  useTestOcrProvider,
  useUpdateOcrProvider,
} from '../hooks/useOcrProviders';

function kindLabel(kind: OcrProviderResponse['kind']): string {
  return kind === OcrProviderKinds.Mistral ? 'Mistral' : 'Azure DI';
}

export function OcrProvidersPage(): JSX.Element {
  const { data: providers, isLoading, isError } = useOcrProviders();
  const pushToast = useToastStore((s) => s.push);

  const createMutation = useCreateOcrProvider();
  const updateMutation = useUpdateOcrProvider();
  const deleteMutation = useDeleteOcrProvider();
  const testMutation = useTestOcrProvider();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<OcrProviderResponse | null>(null);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Proveedores OCR</h1>
          <p className="mt-1 text-sm text-slate-500">
            Motores OCR multi-proveedor (Mistral en Azure AI Foundry) asignados por plan de licencia.
            Azure Document Intelligence sigue siendo el motor por defecto por licencia.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
          Añadir proveedor
        </button>
      </header>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          No se pudo cargar la lista.
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Tipo</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Modelo</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Planes</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 animate-pulse rounded bg-slate-100" />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              (providers ?? []).map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.displayName}</td>
                  <td className="px-4 py-3 text-slate-600">{kindLabel(r.kind)}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-slate-600">{r.modelId ?? '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.assignedLicenses.map((l) => (
                        <PlanBadge key={l} plan={l} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        r.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {r.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-primary disabled:opacity-50"
                        disabled={testMutation.isPending}
                        onClick={() => {
                          testMutation.mutate(r.id, {
                            onSuccess: (res) => {
                              const tone = res.success ? 'success' : 'error';
                              const title = res.success
                                ? `Test OK · ${res.latencyMs ?? 0} ms`
                                : 'Test fallido';
                              pushToast(
                                res.success || !res.errorMessage
                                  ? { tone, title }
                                  : { tone, title, description: res.errorMessage },
                              );
                            },
                            onError: (err) =>
                              pushToast({ tone: 'error', title: 'Error', description: err.message }),
                          });
                        }}
                      >
                        {testMutation.isPending && testMutation.variables === r.id
                          ? 'Probando…'
                          : 'Test'}
                      </button>
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-primary"
                        onClick={() => setEditingProvider(r)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="text-xs text-rose-600 hover:text-rose-800"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (!confirm(`¿Eliminar "${r.displayName}"?`)) return;
                          deleteMutation.mutate(r.id, {
                            onSuccess: () => pushToast({ tone: 'success', title: 'Proveedor eliminado' }),
                            onError: (err) =>
                              pushToast({ tone: 'error', title: 'Error', description: err.message }),
                          });
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <OcrProviderForm
          mode="create"
          busy={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onCreate={(req) => {
            createMutation.mutate(req, {
              onSuccess: () => {
                pushToast({ tone: 'success', title: 'Proveedor creado' });
                setCreateOpen(false);
              },
              onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
            });
          }}
        />
      ) : null}

      {editingProvider !== null ? (
        <OcrProviderForm
          mode="edit"
          provider={editingProvider}
          busy={updateMutation.isPending}
          onClose={() => setEditingProvider(null)}
          onUpdate={(req) => {
            updateMutation.mutate(
              { id: editingProvider.id, request: req },
              {
                onSuccess: () => {
                  pushToast({ tone: 'success', title: 'Proveedor actualizado' });
                  setEditingProvider(null);
                },
                onError: (err) =>
                  pushToast({ tone: 'error', title: 'Error', description: err.message }),
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}
